import React, { useState, useEffect, useCallback } from 'react';
import { Ticket, Search, RefreshCw, Plus, Percent, Trash2, Edit2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function PromoCodesTab({ authHeaders }) {
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'codes', 'campaigns'
  
  // Pagination & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Generate codes form
  const [generateCount, setGenerateCount] = useState(100);
  const [generateDiscount, setGenerateDiscount] = useState(10);
  const [generating, setGenerating] = useState(false);
  
  // Campaign discount editing
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [newDiscount, setNewDiscount] = useState(10);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/promo-codes/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchCodes = useCallback(async () => {
    try {
      let params = `page=${page}&limit=50`;
      if (statusFilter) params += `&status=${statusFilter}`;
      if (searchQuery) params += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await api.get(`/admin/promo-codes?${params}`);
      setCodes(res.data.codes || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch codes:', err);
    }
    setLoading(false);
  }, [page, statusFilter, searchQuery]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await api.get('/admin/campaigns/discounts');
      setCampaigns(res.data);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    }
  }, []);

  useEffect(() => {
    if (authHeaders?.Authorization) {
      fetchStats();
      fetchCampaigns();
    }
  }, [authHeaders, fetchStats, fetchCampaigns]);

  useEffect(() => {
    if (authHeaders?.Authorization && activeView === 'codes') {
      fetchCodes();
    }
  }, [authHeaders, activeView, fetchCodes]);

  const generateCodes = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/admin/promo-codes/generate?count=${generateCount}&discount_percent=${generateDiscount}`);
      toast.success(`Generated ${res.data.generated} codes with ${res.data.discount_percent}% discount`);
      fetchStats();
      if (activeView === 'codes') fetchCodes();
    } catch (err) {
      toast.error('Failed to generate codes');
    }
    setGenerating(false);
  };

  const updateCampaignDiscount = async (campaignId) => {
    try {
      await api.put(`/admin/campaigns/${campaignId}/discount`, { discount_percent: newDiscount });
      toast.success(`Campaign ${campaignId} discount updated to ${newDiscount}%`);
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to update discount');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl text-foreground">Promo Codes</h1>
          <p className="text-muted-foreground">Manage discount codes and campaign settings</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={activeView === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveView('overview')}
            size="sm"
          >
            Overview
          </Button>
          <Button
            variant={activeView === 'codes' ? 'default' : 'outline'}
            onClick={() => setActiveView('codes')}
            size="sm"
          >
            All Codes
          </Button>
          <Button
            variant={activeView === 'campaigns' ? 'default' : 'outline'}
            onClick={() => setActiveView('campaigns')}
            size="sm"
          >
            Campaign Discounts
          </Button>
        </div>
      </div>

      {/* Pool Status Banner */}
      {stats && (
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-4 border border-primary/20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-medium text-foreground flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                Campaign Promo Code Pool
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Unique codes automatically assigned to campaign signups
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center px-4 py-2 bg-card rounded-lg border">
                <div className="text-2xl font-bold text-primary">{stats.codes_remaining?.toLocaleString() || stats.available_codes?.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">Codes Remaining</div>
              </div>
              <div className="text-center px-4 py-2 bg-card rounded-lg border">
                <div className="text-2xl font-bold text-foreground">{stats.total_codes?.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">of 10,000 Pool</div>
              </div>
            </div>
          </div>
          {/* Progress bar showing pool usage */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Pool Usage</span>
              <span>{stats.pool_percentage || Math.round((stats.total_codes / 10000) * 100)}% filled</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${stats.pool_percentage || Math.round((stats.total_codes / 10000) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">{stats.total_codes?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Total Codes</div>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-500">{stats.available_codes?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Available</div>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-500">{stats.assigned_codes?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Assigned</div>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-500/10 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-violet-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-violet-500">{stats.used_codes?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Redeemed</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overview View */}
      {activeView === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Discount Breakdown */}
          <div className="bg-card rounded-xl p-6 border border-border">
            <h3 className="font-medium text-lg text-foreground mb-4 flex items-center gap-2">
              <Percent className="w-5 h-5" />
              Discount Breakdown
            </h3>
            {stats?.discount_breakdown && (
              <div className="space-y-3">
                {Object.entries(stats.discount_breakdown).map(([percent, count]) => (
                  <div key={percent} className="flex items-center justify-between">
                    <span className="text-foreground font-medium">{percent}% off</span>
                    <span className="text-muted-foreground">{count.toLocaleString()} codes</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Generate New Codes */}
          <div className="bg-card rounded-xl p-6 border border-border">
            <h3 className="font-medium text-lg text-foreground mb-2 flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Codes to Pool
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Generate additional codes to expand your promo code pool for campaigns
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Number of Codes</Label>
                  <Input
                    type="number"
                    value={generateCount}
                    onChange={(e) => setGenerateCount(parseInt(e.target.value) || 100)}
                    min={1}
                    max={10000}
                  />
                  <p className="text-xs text-muted-foreground">Max: 10,000 per batch</p>
                </div>
                <div className="space-y-2">
                  <Label>Discount %</Label>
                  <Input
                    type="number"
                    value={generateDiscount}
                    onChange={(e) => setGenerateDiscount(parseInt(e.target.value) || 10)}
                    min={1}
                    max={100}
                  />
                </div>
              </div>
              <Button
                onClick={generateCodes}
                disabled={generating}
                className="w-full"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add {generateCount.toLocaleString()} Codes to Pool
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* All Codes View */}
      {activeView === 'codes' && (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-border flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search code..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="assigned">Assigned</option>
              <option value="used">Used</option>
            </select>
            <Button variant="outline" size="sm" onClick={fetchCodes}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Code</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Discount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Assigned To</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground">Loading...</td>
                  </tr>
                ) : codes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground">No codes found</td>
                  </tr>
                ) : (
                  codes.map((code) => (
                    <tr key={`code-${code.code}`} className="border-t border-border hover:bg-muted/30">
                      <td className="py-3 px-4 font-mono font-medium text-foreground">{code.code}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 rounded-full text-xs bg-primary/10 text-primary font-medium">
                          {code.discount_percent || 10}% off
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {code.is_used ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-violet-500/10 text-violet-500">Redeemed</span>
                        ) : code.assigned_to_signup_id || code.assigned_to_phone ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-amber-500/10 text-amber-500">Assigned</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-500/10 text-green-500">Available</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {code.assigned_to_phone || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatDate(code.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Campaign Discounts View */}
      {activeView === 'campaigns' && (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-medium text-foreground">Campaign Discount Settings</h3>
            <p className="text-sm text-muted-foreground">Configure discount percentage for each campaign</p>
          </div>
          <div className="divide-y divide-border">
            {campaigns.map((campaign) => (
              <div key={campaign.campaign_id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-foreground">
                    Campaign {campaign.campaign_id}: {campaign.title}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Theme: {campaign.theme}
                    {campaign.is_custom && (
                      <span className="ml-2 px-2 py-0.5 rounded text-xs bg-amber-500/10 text-amber-500">Custom</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {editingCampaign === campaign.campaign_id ? (
                    <>
                      <Input
                        type="number"
                        value={newDiscount}
                        onChange={(e) => setNewDiscount(parseInt(e.target.value) || 10)}
                        min={1}
                        max={100}
                        className="w-20"
                      />
                      <span className="text-muted-foreground">%</span>
                      <Button size="sm" onClick={() => updateCampaignDiscount(campaign.campaign_id)}>
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingCampaign(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="px-3 py-1 rounded-full text-sm bg-primary/10 text-primary font-medium">
                        {campaign.discount_percent}% off
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingCampaign(campaign.campaign_id);
                          setNewDiscount(campaign.discount_percent);
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
